import { useMemo, useState } from 'react'
import { OrderSummaryMeals } from './components/OrderSummaryMeals/OrderSummaryMeals'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { OrderDetail } from '../OrderSummary/components/OrderDetail/OrderDetail'
import { OrderDelivery } from './components/OrderDelivery/OrderDelivery'
import BackArrow from 'src/assets/icons/back_arrow.png'
import './OrderSummaryV2.scss'
import { OrderSummaryMealsModal } from './components/OrderSummaryMealsModal/OrderSummaryMealsModal'
import { OrderSummaryGutter } from './components/OrderSummaryGutter/OrderSummaryGutter'
import tracking from 'src/shared/tracking'
import { splitMealsAndAddons } from './utils'
import GiftsSection from 'src/componentsV2/GiftsSection/GiftsSection'
import { FreePremiumMealsBanner } from 'src/componentsV2/FreePremiumMeals/FreePremiumMealsBanner/FreePremiumMealsBanner'
import { FreePremiumMealsBannerEnum } from 'src/componentsV2/FreePremiumMeals/utils'
import { FreePremiumMealsModal } from 'src/componentsV2/FreePremiumMeals/FreePremiumMealsModal/FreePremiumMealsModal'
import { useFreePremiumMeals } from 'src/componentsV2/FreePremiumMeals/useFreePremiumMeals'
import { WineDeliveryDisclaimer } from 'src/componentsV2/Wines/WineDeliveryDisclaimer/WineDeliveryDisclaimer'
import { useIsMobile } from 'src/hooks/useIsMobile'
import { useWineDeliveryDate } from '../../hooks/useWineDeliveryDate'
import { isWineOrContainsWine } from '../../utils'

export const OrderSummaryV2 = ({
  selectedDate,
  products,
  plan,
  timeSlots,
  selectedTimeSlot,
  address,
  handleFieldChange,
  onEditItemsClick,
  onConfirm,
  onBack = undefined
}) => {
  const [meals, addons] = splitMealsAndAddons(products)
  const [modalOpen, setModalOpen] = useState(false)
  const [premiumMealsModalOpen, setPremiumMealsModalOpen] = useState(false)
  const {
    allFreePremiumMealsAdded,
    showFreePremiumMealsBanner,
    showFreePremiumMealsModal
  } = useFreePremiumMeals()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const formattedDate = moment(selectedDate)
    .format('dddd, MMM. Do')
    .replace('May.', 'May')

  const onEditItemsClickMobileHandler = () => {
    tracking.track('CTA Button Clicked', {
      cta_text: 'Back'
    })
    setModalOpen(false)
    onEditItemsClick()
  }

  const onBackClick = () => {
    tracking.track('CTA Button Clicked', {
      cta_text: 'Back'
    })
    if (onBack) {
      onBack()
    } else {
      onEditItemsClick()
    }
  }

  const hasWine = useMemo(
    () => [...meals, ...addons].some(product => isWineOrContainsWine(product)),
    [meals, addons]
  )

  const wineDeliveryDate = useWineDeliveryDate({ hasWine })

  return (
    <>
      <div className="order-summary">
        <button className="order-summary__back" onClick={() => onBackClick()}>
          <img src={BackArrow} alt="back" />
        </button>
        <div className="order-summary__details">
          <div className="order-summary__details-title">
            <button
              className="order-summary__inline-back"
              onClick={() => onBackClick()}>
              <img src={BackArrow} alt="back" />
            </button>
            {`${t('orderSummary.confirm_your_order_for')} ${formattedDate}`}
          </div>
          {hasWine && (
            <WineDeliveryDisclaimer
              size="xl"
              title="Important: This order contains wine"
              body={`Alcohol orders are delivered separately from meals and must be received by an adult 21+ with a valid ID. Estimated arrival: ${wineDeliveryDate}.`}
              origin="wine_added_in_confirmation_page"
            />
          )}
          {isMobile && showFreePremiumMealsBanner && (
            <FreePremiumMealsBanner
              status={
                allFreePremiumMealsAdded
                  ? FreePremiumMealsBannerEnum.CONFIRMED
                  : FreePremiumMealsBannerEnum.CLAIM
              }
              onButtonClick={() => setPremiumMealsModalOpen(true)}
            />
          )}
          <OrderDetail meals={meals} addons={addons} plan={plan} />
          <OrderDelivery
            isExpressOrder={false}
            hasWine={hasWine}
            timeSlots={timeSlots}
            selectedTimeSlot={selectedTimeSlot}
            address={address}
            handleFieldChange={handleFieldChange}
          />
          {hasWine && (
            <span className="order-summary__third-party-order">
              {t('orderSummary.third_party_order')}
            </span>
          )}
        </div>
        <div className="horizontal-divider-line"></div>
        <div className="order-summary__meals">
          <div className="free-premium-meals-banner-container">
            {!isMobile && showFreePremiumMealsBanner && (
              <FreePremiumMealsBanner
                status={
                  allFreePremiumMealsAdded
                    ? FreePremiumMealsBannerEnum.CONFIRMED
                    : FreePremiumMealsBannerEnum.CLAIM
                }
                onButtonClick={() => setPremiumMealsModalOpen(true)}
              />
            )}
          </div>
          <OrderSummaryMeals
            meals={meals}
            addons={addons}
            onEditItems={onEditItemsClick}
          />
          <GiftsSection />
        </div>

        {isMobile && (
          <OrderSummaryMealsModal
            isOpen={modalOpen}
            meals={meals}
            addons={addons}
            onClose={() => setModalOpen(false)}
            onEditItems={
              onEditItemsClick ? onEditItemsClickMobileHandler : undefined
            }
          />
        )}

        {showFreePremiumMealsModal && premiumMealsModalOpen && (
          <FreePremiumMealsModal
            isOpen={premiumMealsModalOpen}
            onCloseClick={() => setPremiumMealsModalOpen(false)}
          />
        )}
      </div>

      <OrderSummaryGutter
        onConfirm={onConfirm}
        onViewSelection={() => setModalOpen(true)}
      />
    </>
  )
}
